import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";
import { SUMMARY_STRUCTURE_CLIENT_VERSION } from "./summaryStructure";

export type ProjectSummaryPresentation =
  components["schemas"]["ProjectSummaryPresentation"];
export type ReplaceProjectSummaryPresentationRequest =
  components["schemas"]["ReplaceProjectSummaryPresentationRequest"];
export type SummaryPresentationSection =
  components["schemas"]["SummaryPresentationSection"];
export type SummaryPresentationTypography =
  components["schemas"]["SummaryPresentationTypography"];

/** Read the renderer state bound to the exact Summary structure this build renders. */
export function useSummaryPresentationQuery(
  projectId: string,
): UseQueryResult<ProjectSummaryPresentation> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectSummaryPresentation(projectId),
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/summary-presentation", {
          params: {
            path: { projectId },
            query: {
              expectedRegistryVersion: SUMMARY_STRUCTURE_CLIENT_VERSION,
            },
          },
          signal,
        }),
      );
    },
    enabled: projectId.length > 0,
  });
}

/**
 * Replace the complete presentation document under both registry and Project
 * compare-and-set identities. The caller supplies the version it actually
 * edited; this hook never re-reads a token at submit time.
 */
export function useReplaceSummaryPresentationMutation(
  projectId: string,
): UseMutationResult<
  ProjectSummaryPresentation,
  unknown,
  ReplaceProjectSummaryPresentationRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.PUT("/v1/projects/{projectId}/summary-presentation", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    onSuccess(saved) {
      queryClient.setQueryData(
        queryKeys.projectSummaryPresentation(projectId),
        saved,
      );
    },
    onError(error) {
      const failure = classifySummaryPresentationWriteFailure(error);
      // A stale refusal proves the cached version moved. A transport/5xx
      // failure is ambiguous: the server may have committed before the reply
      // was lost. Refresh in both cases, but never resend automatically.
      if (failure.kind === "stale" || failure.kind === "ambiguous") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.projectSummaryPresentation(projectId),
        });
      }
    },
    retry: false,
  });
}

export type SummaryPresentationReadFailure =
  | { kind: "structure_mismatch"; currentRegistryVersion: string | null }
  | {
      kind: "stored_registry_mismatch";
      currentRegistryVersion: string | null;
      storedRegistryVersion: string | null;
    }
  | { kind: "denied" }
  | { kind: "unavailable" }
  | { kind: "upstream" }
  | { kind: "error"; code: string; requestId: string | undefined };

export type SummaryPresentationWriteFailure =
  | { kind: "stale"; currentPresentationVersion: string | null }
  | SummaryPresentationReadFailure
  | { kind: "invalid"; code: string; requestId: string | undefined }
  | { kind: "ambiguous"; requestId: string | undefined };

function envelopeString(error: ApiError, key: string): string | null {
  const value = error.envelope[key];
  return typeof value === "string" ? value : null;
}

export function classifySummaryPresentationReadFailure(
  error: unknown,
): SummaryPresentationReadFailure {
  if (!(error instanceof ApiError)) {
    return {
      kind: "error",
      code: "upstream_unavailable",
      requestId: undefined,
    };
  }
  if (error.isNotImplemented) return { kind: "unavailable" };
  if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
    return { kind: "denied" };
  }
  if (error.code === "summary_structure_version_mismatch") {
    return {
      kind: "structure_mismatch",
      currentRegistryVersion: envelopeString(
        error,
        "currentSummaryStructureVersion",
      ),
    };
  }
  if (error.code === "summary_presentation_registry_mismatch") {
    return {
      kind: "stored_registry_mismatch",
      currentRegistryVersion: envelopeString(
        error,
        "currentSummaryStructureVersion",
      ),
      storedRegistryVersion: envelopeString(
        error,
        "storedSummaryStructureVersion",
      ),
    };
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return { kind: "upstream" };
  }
  return { kind: "error", code: error.code, requestId: error.requestId };
}

export function classifySummaryPresentationWriteFailure(
  error: unknown,
): SummaryPresentationWriteFailure {
  if (!(error instanceof ApiError)) {
    return { kind: "ambiguous", requestId: undefined };
  }
  if (error.code === "stale_summary_presentation_version") {
    return {
      kind: "stale",
      currentPresentationVersion: envelopeString(
        error,
        "currentSummaryPresentationVersion",
      ),
    };
  }
  if (error.status >= 500 && !error.isNotImplemented) {
    return { kind: "ambiguous", requestId: error.requestId };
  }
  if (error.status === 400 || error.status === 413 || error.status === 422) {
    return { kind: "invalid", code: error.code, requestId: error.requestId };
  }
  return classifySummaryPresentationReadFailure(error);
}
